import { toCyrillic } from './cyrillic'
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'

/**
 * Internationalisation, without a dependency.
 *
 * - Six languages: English (the default), Deutsch, Crnogorski, Bosanski and Srpski in both
 *   scripts — latinica and ћирилица. The BCS catalogs are Latin (Serbian ekavian, the others
 *   ijekavian); Serbian Cyrillic has no catalog of its own and is transliterated from the
 *   Serbian Latin one at lookup time (see cyrillic.ts), so the two scripts never drift apart.
 * - Strings live next to the feature that shows them, one file per feature under
 *   `i18n/messages/`, all five languages side by side. `defineMessages` makes TypeScript refuse
 *   a language that is missing a key (or has one English doesn't), so nothing ships
 *   half-translated.
 * - Numbers and dates follow the language (`fmtNumber`, `fmtDate` …). Data is never translated:
 *   project names, AU column labels and the Excel export's workbook headers stay as AU has them.
 * - The choice is remembered on the device (localStorage), and sets <html lang>.
 */

export type Lang = 'en' | 'de' | 'cnr' | 'bs' | 'sr' | 'sr-Cyrl'

/** The languages with a catalog of their own; sr-Cyrl is derived from sr. */
type CatalogLang = Exclude<Lang, 'sr-Cyrl'>

export const LANGUAGES: readonly { code: Lang; label: string; short: string; intl: string }[] = [
  { code: 'en', label: 'English', short: 'EN', intl: 'en-GB' },
  { code: 'de', label: 'Deutsch', short: 'DE', intl: 'de-DE' },
  { code: 'cnr', label: 'Crnogorski', short: 'CG', intl: 'sr-Latn-ME' },
  { code: 'bs', label: 'Bosanski', short: 'BS', intl: 'bs-Latn-BA' },
  { code: 'sr', label: 'Srpski (latinica)', short: 'SR', intl: 'sr-Latn-RS' },
  { code: 'sr-Cyrl', label: 'Српски (ћирилица)', short: 'СР', intl: 'sr-Cyrl-RS' },
]

const DEFAULT_LANG: Lang = 'en'
const STORAGE_KEY = 'aufmass:lang'

/** A plain string, or plural forms. BCS needs `few` (2–4: "3 kolone"); English and German don't. */
export type Plural = { one: string; few?: string; other: string }
export type Message = string | Plural

type Catalog<K extends string> = Record<K, Message>

/**
 * A feature's strings in all five languages. English is the source: every other language must
 * have exactly its keys, which TypeScript enforces here at the definition.
 */
export function defineMessages<const K extends string>(m: {
  en: Catalog<K>
  de: NoInfer<Catalog<K>>
  cnr: NoInfer<Catalog<K>>
  bs: NoInfer<Catalog<K>>
  sr: NoInfer<Catalog<K>>
}) {
  return m
}

export type Messages<K extends string> = ReturnType<typeof defineMessages<K>>

// The current language, readable outside React (number formatting, module-level helpers). The
// provider keeps it in step and remounts the app on a change, so everything re-renders with it.
let current: Lang = readStored()

function readStored(): Lang {
  try {
    const v = localStorage.getItem(STORAGE_KEY)
    return LANGUAGES.some((l) => l.code === v) ? (v as Lang) : DEFAULT_LANG
  } catch {
    return DEFAULT_LANG
  }
}

export function getLang(): Lang {
  return current
}

/**
 * Formatting stand-ins for engines that list a locale but ship it without its data. Chrome (and so
 * Android) "supports" bs but formats it as 1,234.50 with months "M08"; Serbian-Latin for Bosnia
 * has the same separators (1.234,50 €), the same plural rules (one / few / other) and month names
 * that differ only in spelling (avg / aug). Safari has real Bosnian data and keeps it.
 */
const INTL_FALLBACK: Partial<Record<Lang, string>> = { bs: 'sr-Latn-BA', cnr: 'sr-Latn', sr: 'sr-Latn', 'sr-Cyrl': 'sr' }

const resolved = new Map<Lang, string>()

/** True when the engine really formats this locale: a decimal comma where the language uses one. */
function hasLocaleData(locale: string): boolean {
  try {
    return new Intl.NumberFormat(locale, { minimumFractionDigits: 1 }).format(1.5).includes(',')
  } catch {
    return false
  }
}

/** The Intl locale for the current language (en-GB, de-DE, sr-Latn-ME, bs-Latn-BA — or a stand-in). */
export function intlLocale(lang: Lang = current): string {
  let r = resolved.get(lang)
  if (!r) {
    const primary = LANGUAGES.find((l) => l.code === lang)!.intl
    const fallback = INTL_FALLBACK[lang]
    r = lang === 'en' || !fallback || hasLocaleData(primary) ? primary : fallback
    resolved.set(lang, r)
  }
  return r
}

const I18nContext = createContext<{ lang: Lang; setLang: (l: Lang) => void }>({
  lang: DEFAULT_LANG,
  setLang: () => {},
})

export function I18nProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<Lang>(current)

  const setLang = useCallback((l: Lang) => {
    current = l
    try { localStorage.setItem(STORAGE_KEY, l) } catch { /* private mode: lasts the session */ }
    setLangState(l)
  }, [])

  useEffect(() => {
    document.documentElement.lang = lang
  }, [lang])

  const value = useMemo(() => ({ lang, setLang }), [lang, setLang])
  // Keyed by language: a switch remounts the tree, so plain helpers (fmtNumber, formatValue)
  // that read the module-level language re-render too. Query data survives — its cache lives
  // above this provider — so a switch costs a re-render, not a reload.
  return (
    <I18nContext.Provider value={value}>
      <div key={lang} className="contents">{children}</div>
    </I18nContext.Provider>
  )
}

export function useLang() {
  return useContext(I18nContext)
}

type Vars = Record<string, string | number>

/**
 * The translate function for one feature's messages:
 *
 *     const t = useT(rtkMessages)
 *     t('filters')                         // "Filters"
 *     t('routesShown', { shown: 3, total: 9 })  // "{shown} of {total} routes" → "3 of 9 routes"
 *     t('crews', { count: 3 })             // plural: "3 crews" / "3 kolone"
 *
 * Keys are type-checked against the feature's English catalog.
 */
export function useT<K extends string>(messages: Messages<K>) {
  const { lang } = useLang()
  return useMemo(() => translator(messages, lang), [messages, lang])
}

/** The same, outside React (current language). */
export function translator<K extends string>(messages: Messages<K>, lang: Lang = current) {
  const rules = new Intl.PluralRules(intlLocale(lang))
  return (key: K, vars?: Vars): string => {
    // Serbian Cyrillic reads the Serbian Latin catalog and transliterates the template — before
    // the variables go in, so names and projects (data) are never converted.
    const cyr = lang === 'sr-Cyrl'
    const source: CatalogLang = cyr ? 'sr' : lang
    const msg: Message = messages[source][key] ?? messages.en[key]
    let text: string
    if (typeof msg === 'string') {
      text = msg
    } else {
      const n = typeof vars?.count === 'number' ? vars.count : 0
      const form = rules.select(n)
      text = (form === 'one' ? msg.one : form === 'few' ? (msg.few ?? msg.other) : msg.other)
    }
    if (cyr) text = toCyrillic(text)
    if (!vars) return text
    return text.replace(/\{(\w+)\}/g, (m, name: string) => {
      const v = vars[name]
      if (v === undefined) return m
      return typeof v === 'number' ? fmtNumber(v) : v
    })
  }
}

// --- formatting ---------------------------------------------------------------------------

const nfCache = new Map<string, Intl.NumberFormat>()
function nf(digits: number, extra?: Intl.NumberFormatOptions): Intl.NumberFormat {
  const key = `${current}|${digits}|${JSON.stringify(extra ?? {})}`
  let f = nfCache.get(key)
  if (!f) {
    f = new Intl.NumberFormat(intlLocale(), { maximumFractionDigits: digits, ...extra })
    nfCache.set(key, f)
  }
  return f
}

/** 170.881 (de, cnr, bs, sr, sr-Cyrl) · 170,881 (en). */
export function fmtNumber(n: number, maxFractionDigits = 0): string {
  return nf(maxFractionDigits).format(n)
}

/** 32.225.084 € (de, cnr, bs, sr, sr-Cyrl) · €32,225,084 (en). */
export function fmtEur(n: number, maxFractionDigits = 0): string {
  return nf(maxFractionDigits, { style: 'currency', currency: 'EUR' }).format(n)
}

/**
 * A calendar date from the API (yyyy-MM-dd or an ISO timestamp) as the reader writes it:
 * 30.09.2026 (de, cnr, bs, sr, sr-Cyrl) · 30/09/2026 (en). Parsed by hand, never through Date, so a
 * timezone can never move it by a day.
 */
export function fmtDate(iso: string | null | undefined): string {
  if (!iso) return '—'
  const [y, m, d] = iso.slice(0, 10).split('-')
  if (!y || !m || !d) return iso
  return current === 'en' ? `${d}/${m}/${y}` : `${d}.${m}.${y}`
}

/** A timestamp with time of day, in the reader's language and timezone. */
export function fmtDateTime(iso: string | null | undefined): string {
  if (!iso) return '—'
  const dt = new Date(iso)
  if (Number.isNaN(dt.getTime())) return iso
  return new Intl.DateTimeFormat(intlLocale(), {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
  }).format(dt)
}
